import React, { memo, useRef } from "react";
import { Table as ReactstrapTable } from "reactstrap";
import { compose } from "recompose";
import { withTable } from "components/common";
import { withXeroErrors, withDocumentRenderer } from "components";
import { Trans, withTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { formatCurrency, formatDate } from "services/util";
import Search from "./Search";
import { ALL, DRAFT, SENT } from "./constants/statuses";
import { useQuoteStatuses } from "./hooks/useQuoteStatuses";
import cx from "classnames";
import moment from "moment";
import { QuoteDocument } from "components/documents";
import { useOrganisationContext } from "hooks";
import { TruncateTd } from "components/common/Tables";

const EmailCell = withDocumentRenderer({
  onDownload: () => (getDocument, item) =>
    getDocument(
      {
        item:{
          ...item,
          LineItems: item.LineItems.map((val) => ({
            ...val,
            UnitAmount: item.LineAmountTypes === 'INCLUSIVE' ? ((val.LineAmount + val.TaxAmount + (val.DiscountAmount ?? 0)) / val.Quantity).toFixed(2) : val.UnitAmount,
            LineAmount: item.LineAmountTypes === 'INCLUSIVE' ? val.LineAmount + val.TaxAmount : val.LineAmount,
          }))
        }
      },
      `quote_${item.QuoteNumber}.pdf`
    ),
  type: "QUOTE",
})(({ EmailDocument, DocumentRenderer, i, selectedOrganisation }) => { 

  i = {
    ...i,
    LineItems: i?.LineItems.map((val) => ({
      ...val,
      UnitAmount: i?.LineAmountTypes === 'INCLUSIVE' ? ((val.LineAmount + val.TaxAmount + (val.DiscountAmount ?? 0)) / val.Quantity).toFixed(2) : val.UnitAmount,
      LineAmount: i?.LineAmountTypes === 'INCLUSIVE' ? val.LineAmount + val.TaxAmount : val.LineAmount,
    })),
  }

  return (
    <>
      <EmailDocument compact item={i} type="QUOTE" />
      <DocumentRenderer component={QuoteDocument} selectedOrganisation={selectedOrganisation}/>
    </>
  )
});

const DownloadCell = withDocumentRenderer({
  onDownload: () => (getDocument, item) =>
    getDocument(
      {
        item:{
          ...item,
          LineItems: item.LineItems.map((val) => ({
            ...val,
            UnitAmount: item.LineAmountTypes === 'INCLUSIVE' ? ((val.LineAmount + val.TaxAmount + (val.DiscountAmount ?? 0)) / val.Quantity).toFixed(2) : val.UnitAmount,
            LineAmount: item.LineAmountTypes === 'INCLUSIVE' ? val.LineAmount + val.TaxAmount : val.LineAmount,
          }))
        }
      },
      `quote_${item.QuoteNumber}.pdf`
    ),
  type: "QUOTE",
})(({ DownloadDocument, DocumentRenderer, i, selectedOrganisation }) => { 

  i = {
    ...i,
    LineItems: i?.LineItems.map((val) => ({
      ...val,
      UnitAmount: i?.LineAmountTypes === 'INCLUSIVE' ? ((val.LineAmount + val.TaxAmount + (val.DiscountAmount ?? 0)) / val.Quantity).toFixed(2) : val.UnitAmount,
      LineAmount: i?.LineAmountTypes === 'INCLUSIVE' ? val.LineAmount + val.TaxAmount : val.LineAmount,
    })),
  }

  return (
    <>
      <DownloadDocument compact item={i} className="mr-1" />
      <DocumentRenderer component={QuoteDocument} selectedOrganisation={selectedOrganisation}/>
    </>
  )}
);


const ExpiryCell = memo(({ date }) => {
  return (
    <td
      className={cx({
        "text-danger": moment(date).isBefore(),
      })}
    >
      {formatDate(date)}
    </td>
  );
});

let _QuotesTable = ({
  XeroErrors,
  Pagination,
  TableContainer,
  ViewingRecords,
  XeroTableDataFetch,
  data,
  setFilters,
  status,
  filters,
}) => {
  const statuses = useQuoteStatuses();
  const initialFilters = useRef(filters);
  const {selectedOrganisation} = useOrganisationContext()

  return (
    <div className="inv-table">
      <XeroErrors color="warning" />
      <Search
        onFiltersChanged={(v) => {
          setFilters(v === null ? initialFilters.current : v);
        }}
        quote
        status={status}
      />
      
      <div className="small">
        <TableContainer idKey="InvoiceID">
          <ReactstrapTable size="sm" className="small" responsive>
            <thead>
              <tr>
                <th select />
                <th sortBy="InvoiceNumber">
                  <Trans>Number</Trans>
                </th>
                <th sortBy="Reference">
                  <Trans>Reference</Trans>
                </th>
                <th sortBy="Contact.Name">
                  <Trans>Customer</Trans>
                </th>
                <th sortBy="Date">
                  <Trans>Issue Date</Trans>
                </th>
                {status === ALL || status === DRAFT || status === SENT ? (
                  <th sortBy="ExpiryDate">
                    <Trans>Expiry Date</Trans>
                  </th>
                ) : null}
                {/* {status === ALL && ( */}
                  <th sortBy="Status">
                    <Trans>Status</Trans>
                  </th>
                {/* )} */}
                <th className="text-nowrap" sortBy="Total">
                  <Trans>Amount</Trans>
                </th>
                <th className="text-nowrap">
                  <Trans>Actions</Trans>
                  </th>
              </tr>
            </thead>
            <tbody>
              {data.loading && (
                <tr>
                  <td colSpan="9" className="text-center">
                    <span className="fa fa-spinner fa-spin mt-2" /> <Trans>Loading...</Trans>
                  </td>
                </tr>
              )}
              {data.results.map((i, idx) => (
                <tr key={i.QuoteID}>
                  <td select />
                  <td>
                    <Link to={`/quotes/view/${i.QuoteID}`}>
                      {i.QuoteNumber}
                    </Link>
                  </td>
                  <TruncateTd content={i.Reference} _key={`reference_${idx}`} className="truncate"> 
                    {i.Reference || ("-")}
                  </TruncateTd>
                  <TruncateTd content={i.Contact?.Name} _key={`contact_${idx}`} className="truncate"> 
                    {i.Contact ? (
                      <Link to={`/contacts/view/${i.Contact.ContactID}`}>
                        {i.Contact.Name}
                      </Link>
                    ) : null}
                  </TruncateTd>
                  <td>{formatDate(i.Date)}</td>
                  {status === ALL || status === DRAFT || status === SENT ? (
                    <>
                    <ExpiryCell date={i.ExpiryDate} />
                    
                    </>
                  ) : null}
                  <td>{statuses[i.Status]}</td>
                  <td>{formatCurrency(i.Total)}</td>
                  <td className="text-nowrap">
                    <DownloadCell i={i} selectedOrganisation={selectedOrganisation}/>
                    <EmailCell i={i} selectedOrganisation={selectedOrganisation}/>
                  </td>
                  {/* {status === ALL && } */}
                </tr>
              ))}
            </tbody>
          </ReactstrapTable>
        </TableContainer>
      </div>
      <XeroTableDataFetch />
      <div className="float-right">
        <Pagination size="sm" noPages />
      </div>
      <div className="clearfix">
        <ViewingRecords noCount className="float-left" />
      </div>
    </div>
  );
};

export const enhance = compose(
  withTranslation(),
  withXeroErrors(),
  withTable({
    limit: 50,
    url: "quotes",
    ordering: {
      column: "Date",
      direction: 2,
    },
    filters: ({ status }) => ({
      extra_params: {
        Status: status === ALL ? undefined : status,
      },
    }),
  }),
  withDocumentRenderer({
    onDownload:
      ({ xero }) =>
      (getDocument, item) => {
        getDocument({ item }, `quote_${item.QuoteNumber}.pdf`);
      },
  })
);

_QuotesTable = enhance(_QuotesTable);

export const QuotesTable = _QuotesTable;
